<script lang="ts">
  /**
   * OpenWaterRouteCard — a big, obvious "See your route" tap target shown only on
   * an open-water swim detail. Tapping calls the native `openRouteMap` bridge
   * method, which presents a full-screen lake-route map OUTSIDE the web view.
   *
   * GATING (the card must NEVER appear where a tap dead-ends):
   *   1. the swim is open water (currentDerivedWorkout.isOpenWater), AND
   *   2. a native handshake listed `openRouteMap` in supportedMethods, AND
   *   3. the bridge is available.
   * In a plain browser or the screenshot harness with no native handshake, the
   * supportedMethods set is empty, so the card is hidden. The harness opts in by
   * having its handshake stub list "openRouteMap" (simulating the native shell).
   */
  import { currentDerivedWorkout, currentRawFirehose } from "../lib/state";
  import { bridge } from "../bridge/client";
  import { supportsMethodStore } from "../lib/nativeMethods";
  import { haptic } from "../lib/haptics";

  const canOpenRoute = supportsMethodStore("openRouteMap");

  interface Props {
    /** Override the route target; when omitted, uses the current swim's uuid.
     *  Pass `null` explicitly to force "no route" (viewer didn't swim). */
    workoutUUID?: string | null;
    /** Override the open-water flag; when omitted (undefined), uses
     *  currentDerivedWorkout. Pass true/false to force; do NOT pass null
     *  (`?? store` treats null as "not passed"). */
    openWater?: boolean;
  }
  let { workoutUUID, openWater }: Props = $props();

  // `?? store` falls through only on null/undefined; an explicit `false` sticks.
  let isOpenWater = $derived(openWater ?? ($currentDerivedWorkout?.isOpenWater === true));
  // A caller may pass `null` to mean "no route" — distinguish that from "not
  // passed" (undefined → fall back to the store's current swim).
  let uuid = $derived(
    workoutUUID !== undefined ? workoutUUID : ($currentRawFirehose?.workout?.uuid ?? null),
  );
  let visible = $derived(
    isOpenWater && uuid !== null && $canOpenRoute && bridge.isAvailable(),
  );

  async function openRoute() {
    if (!uuid) return;
    haptic("selection");
    try {
      await bridge.openRouteMap(uuid);
    } catch {
      // Native presents/dismisses the map itself; a rejected call is a silent
      // no-op here — there's no in-web fallback to route to.
    }
  }
</script>

{#if visible}
  <button type="button" class="route-card" onclick={openRoute} aria-label="See your route on the lake map">
    <span class="route-icon" aria-hidden="true">
      <!-- Route glyph: start dot → winding dashed path → end ring. The old
           dot-with-drop-line read as a stick figure at 24px (persona gate
           2026-07-11, polish #2). -->
      <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="5" cy="19" r="2" fill="currentColor" stroke="none" />
        <path d="M5 19c7 0 2-7 8-8" stroke-dasharray="3 2.5" />
        <circle cx="18" cy="7" r="3" />
      </svg>
    </span>
    <span class="route-text">
      <span class="route-title">See your route</span>
      <span class="route-sub">Where you swam, on the lake</span>
    </span>
    <span class="route-chev" aria-hidden="true">›</span>
  </button>
{/if}

<style>
  .route-card {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    width: 100%;
    min-height: 64px;
    padding: var(--space-4);
    background: var(--bg-card);
    border: 1px solid var(--divider);
    border-radius: var(--radius-card);
    cursor: pointer;
    text-align: left;
    -webkit-tap-highlight-color: transparent;
    transition: background-color var(--dur-micro) var(--ease-swim);
  }
  .route-card:active {
    background: var(--bg-raised);
  }
  .route-card:focus-visible {
    outline: 2px solid var(--accent-primary);
    outline-offset: 2px;
  }
  .route-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    flex-shrink: 0;
    background: rgba(169, 202, 238, 0.14); /* accent-secondary, quiet fill */
    color: var(--accent-secondary);
  }
  .route-text {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }
  .route-title {
    font-size: 17px;
    font-weight: 600;
    color: var(--text-primary);
    line-height: 1.25;
  }
  .route-sub {
    font-size: 15px;
    color: var(--text-muted);
    line-height: 1.3;
  }
  .route-chev {
    margin-left: auto;
    flex-shrink: 0;
    font-size: 22px;
    line-height: 1;
    color: var(--text-dim);
    font-weight: 400;
  }
</style>
